import { useState } from 'react'
import { Dialog, DialogContent, DialogActions, Button, Stack, Typography, CircularProgress } from '@mui/material'

import { generateCardContent } from '@/utils/generateCardContent'
import { CardActivityType } from '@/models/card-activity/card-activity.model'
import PointsAdjust from '@/components/PointsAdjust'
import { ReactComponent as AIGenerateIcon } from '@/assets/icons/ai-generate-white.svg'
import { ReactComponent as ChattyAI } from '@/assets/icons/chatty-avatar.svg'

interface GenerateTSVDialogProps {
  open: boolean
  onClose: () => void
  onTSVGenerated: (tsvData: string) => void
  cardType: string
  language: string
  translateTo: string
}

export function GenerateTSVDialog({
  open,
  onClose,
  onTSVGenerated,
  cardType,
  language,
  translateTo,
}: GenerateTSVDialogProps) {
  const [prompt, setPrompt] = useState('')
  const [numberOfCards, setNumberOfCards] = useState(10)
  const [isGenerating, setIsGenerating] = useState(false)

  const handleGenerate = async () => {
    if (!prompt.trim()) return

    setIsGenerating(true)
    try {
      const result = await generateCardContent(
        prompt,
        language,
        translateTo,
        mapCardType(cardType as CardActivityType),
        numberOfCards,
      )

      if (!result.error && result.content) {
        onTSVGenerated(result.content)
      }
      onClose()
    } catch (error) {
      console.error('Error generating TSV:', error)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '16px',
          p: '16px 24px',
          background: 'white',
          position: 'relative',
          border: '1px solid transparent',
          backgroundImage: `linear-gradient(white, white), linear-gradient(135deg, #F093FB 0%, #F5576C 100%)`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        },
      }}
      onClose={onClose}
    >
      <Stack alignItems="center" direction="row" gap="1px">
        <ChattyAI height={65} width={65} />
        <Stack>
          <Typography
            gutterBottom
            sx={{
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Generate Page Data
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '400',
            }}
          >
            Ask AI to generate content for your pages
          </Typography>
        </Stack>
      </Stack>

      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <textarea
            autoFocus
            placeholder={
              mapCardType(cardType as CardActivityType) === 'repeat'
                ? 'Describe what kind of vocabulary or phrases you want to generate...'
                : mapCardType(cardType as CardActivityType) === 'question'
                  ? 'Describe what kind of multiple choice questions you want to generate...'
                  : 'Describe what kind of prompts you want to generate...'
            }
            style={{
              display: 'flex',
              padding: '8px',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              flex: '1 0 0',
              alignSelf: 'stretch',
              width: '100%',
              minHeight: '96px', // 4 rows approx
              fontSize: '14px',
              borderRadius: '8px',
              border: '1px solid #ccc',
              resize: 'vertical',
              boxSizing: 'border-box',
              outline: 'none',
            }}
            value={prompt}
            onChange={e => {
              setPrompt(e.target.value)
            }}
          />

          <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1}>
            <Typography fontSize={14} fontWeight={500}>
              Number of Pages
            </Typography>
            <PointsAdjust
              points={numberOfCards}
              // @ts-ignore
              setPoints={(value: number) => {
                setNumberOfCards(value)
              }}
              sx={{
                maxWidth: '50px',
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        {/* <Button onClick={onClose}>Cancel</Button> */}
        <Button
          fullWidth
          disabled={!prompt.trim() || isGenerating}
          startIcon={
            isGenerating ? <CircularProgress color="inherit" size={20} /> : <AIGenerateIcon height={20} width={20} />
          }
          sx={{
            position: 'relative',
            borderRadius: '16px',
            border: '1px solid #F093FB',
            background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
            boxShadow: '0px 1px 20px 0px #FAA7E0',
            color: '#fff',
            fontWeight: 600,
            fontSize: '16px',
            '&:hover': {
              background: 'linear-gradient(135deg, #F093FB 0%, #F5576C 100%)',
              opacity: 0.95,
            },
          }}
          onClick={handleGenerate}
        >
          {isGenerating ? 'Generating...' : 'Generate Prompts'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const mapCardType = (cardType: CardActivityType): 'prompt' | 'question' | 'repeat' | 'short-answer' => {
  switch (cardType) {
    case CardActivityType.READ_RESPOND:
    case CardActivityType.RESPOND_WRITE:
      return 'prompt'
    case CardActivityType.MULTIPLE_CHOICE:
      return 'question'
    case CardActivityType.REPEAT:
    case CardActivityType.READ_REPEAT:
      return 'repeat'
    case CardActivityType.SHORT_ANSWER:
      return 'short-answer'
    default:
      return 'repeat'
  }
}
